import type { CampaignRepeat, CampaignStatus, MarketingCampaign } from '@otw/models/leads/marketingCampaign'
import {
  cloneAudienceFilterGroup,
  emptyAudienceFilter,
  MARKETING_CAMPAIGN_FROM_EMAIL,
  MARKETING_CAMPAIGN_FROM_NAME,
  MARKETING_SEND_BATCH_SIZE,
  parseAudienceFilterGroup,
  parseCampaignRepeat,
  parseReplyToUserIds,
} from '@otw/models/leads/marketingCampaign'
import { db } from '@otw/models/db'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { addDoc, doc, getDoc, getDocs, setDoc } from 'firebase/firestore'
import { useAuth } from 'contexts/AuthContext'
import { useSnackbar } from '@/hooks/common/useSnackbar'
import { portalApi } from '@/core/api/modules/portalApi'
import { campaignRecipientsQueryKey } from './useCampaignRecipients'
import { contactsQueryKey } from './useContacts'

export const marketingCampaignsQueryKey = ['marketing_campaigns'] as const

export interface CreateMarketingCampaignInput {
  name: string
  subject?: string
  htmlBody?: string
  audienceFilter?: MarketingCampaign['audienceFilter']
  savedAudienceId?: string
  replyToUserIds?: string[]
  includedContactIds?: string[]
  excludedContactIds?: string[]
  featuredVehicleIds?: string[]
  featuredVehicleLayout?: 'cards' | 'list'
  scheduledAt?: Date | null
  repeat?: CampaignRepeat
}

export interface UpdateMarketingCampaignInput {
  id: string
  name: string
  subject: string
  htmlBody: string
  audienceFilter: MarketingCampaign['audienceFilter']
  savedAudienceId?: string
  replyToUserIds?: string[]
  includedContactIds?: string[]
  excludedContactIds?: string[]
  featuredVehicleIds?: string[]
  featuredVehicleLayout?: 'cards' | 'list'
  scheduledAt?: Date | null
  repeat?: CampaignRepeat
}

const parseSavedScheduledAt = (value: Date | null | undefined): Date | null => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return null
  return value
}

const sortCampaigns = (campaigns: MarketingCampaign[]): MarketingCampaign[] =>
  [...campaigns].sort((a, b) => b.updatedAt.getTime() - a.updatedAt.getTime())

export const useMarketingCampaigns = () => {
  const { userInfo } = useAuth()
  const { showSuccess, showError } = useSnackbar()
  const queryClient = useQueryClient()

  const {
    data: campaigns,
    isLoading,
    error,
  } = useQuery({
    queryKey: marketingCampaignsQueryKey,
    queryFn: async () => {
      const snapshot = await getDocs(db.marketing_campaigns)
      return sortCampaigns(snapshot.docs.map((d) => d.data()))
    },
    staleTime: 1000 * 60 * 2,
  })

  const createMutation = useMutation({
    mutationFn: async (input: CreateMarketingCampaignInput) => {
      const uid = userInfo?.id
      if (!uid) throw new Error('You must be signed in.')
      const now = new Date()
      const replyToUserIds = parseReplyToUserIds(input.replyToUserIds)
      const payload: MarketingCampaign = {
        id: '',
        name: input.name.trim(),
        type: 'email',
        fromName: MARKETING_CAMPAIGN_FROM_NAME,
        fromEmail: MARKETING_CAMPAIGN_FROM_EMAIL,
        subject: input.subject?.trim() ?? '',
        htmlBody: input.htmlBody ?? '',
        audienceFilter: parseAudienceFilterGroup(
          input.audienceFilter ?? emptyAudienceFilter(),
        ),
        ...(input.savedAudienceId?.trim()
          ? { savedAudienceId: input.savedAudienceId.trim() }
          : {}),
        ...(replyToUserIds.length > 0 ? { replyToUserIds } : {}),
        includedContactIds: parseReplyToUserIds(input.includedContactIds),
        excludedContactIds: parseReplyToUserIds(input.excludedContactIds),
        featuredVehicleIds: parseReplyToUserIds(input.featuredVehicleIds),
        ...(input.featuredVehicleLayout ? { featuredVehicleLayout: input.featuredVehicleLayout } : {}),
        archived: false,
        status: 'draft',
        scheduledAt: parseSavedScheduledAt(input.scheduledAt),
        repeat: parseCampaignRepeat(input.repeat),
        sendOccurrence: 0,
        sentCount: 0,
        failedCount: 0,
        unsubscribedCount: 0,
        sentAt: null,
        createdByUserId: uid,
        createdAt: now,
        updatedAt: now,
      }
      const docRef = await addDoc(db.marketing_campaigns, payload)
      return docRef.id
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: marketingCampaignsQueryKey })
      showSuccess('Campaign draft saved')
    },
    onError: () => showError('Could not create campaign'),
  })

  const updateMutation = useMutation({
    mutationFn: async (input: UpdateMarketingCampaignInput) => {
      const ref = doc(db.marketing_campaigns, input.id)
      const snap = await getDoc(ref)
      const current = snap.data()
      if (!current) throw new Error('Campaign not found')
      if (current.status !== 'draft') {
        throw new Error('Only draft campaigns can be edited.')
      }
      const now = new Date()
      const merged: MarketingCampaign = {
        ...current,
        name: input.name.trim(),
        type: 'email',
        fromName: MARKETING_CAMPAIGN_FROM_NAME,
        fromEmail: MARKETING_CAMPAIGN_FROM_EMAIL,
        subject: input.subject.trim(),
        htmlBody: input.htmlBody,
        audienceFilter: parseAudienceFilterGroup(input.audienceFilter),
        scheduledAt: parseSavedScheduledAt(input.scheduledAt),
        repeat: parseCampaignRepeat(input.repeat),
        updatedAt: now,
      }
      const savedAudienceId = input.savedAudienceId?.trim() ?? ''
      if (savedAudienceId) merged.savedAudienceId = savedAudienceId
      else delete merged.savedAudienceId
      const replyToUserIds = parseReplyToUserIds(input.replyToUserIds)
      if (replyToUserIds.length > 0) merged.replyToUserIds = replyToUserIds
      else delete merged.replyToUserIds
      merged.includedContactIds = parseReplyToUserIds(input.includedContactIds)
      merged.excludedContactIds = parseReplyToUserIds(input.excludedContactIds)
      merged.featuredVehicleIds = parseReplyToUserIds(input.featuredVehicleIds)
      if (input.featuredVehicleLayout) merged.featuredVehicleLayout = input.featuredVehicleLayout
      else delete merged.featuredVehicleLayout
      await setDoc(ref, merged)
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: marketingCampaignsQueryKey })
      showSuccess('Campaign draft updated')
    },
    onError: (err) =>
      showError(err instanceof Error ? err.message : 'Could not update campaign'),
  })

  const duplicateMutation = useMutation({
    mutationFn: async (sourceId: string) => {
      const uid = userInfo?.id
      if (!uid) throw new Error('You must be signed in.')
      const snap = await getDoc(doc(db.marketing_campaigns, sourceId))
      const source = snap.data()
      if (!source) throw new Error('Campaign not found')
      const now = new Date()
      const replyToUserIds = parseReplyToUserIds(source.replyToUserIds)
      const payload: MarketingCampaign = {
        id: '',
        name: `${source.name.trim() || 'Untitled campaign'} (copy)`,
        type: 'email',
        fromName: MARKETING_CAMPAIGN_FROM_NAME,
        fromEmail: MARKETING_CAMPAIGN_FROM_EMAIL,
        subject: source.subject,
        htmlBody: source.htmlBody,
        audienceFilter: cloneAudienceFilterGroup(
          parseAudienceFilterGroup(source.audienceFilter),
        ),
        ...(source.savedAudienceId ? { savedAudienceId: source.savedAudienceId } : {}),
        ...(replyToUserIds.length > 0 ? { replyToUserIds } : {}),
        includedContactIds: parseReplyToUserIds(source.includedContactIds),
        excludedContactIds: parseReplyToUserIds(source.excludedContactIds),
        featuredVehicleIds: parseReplyToUserIds(source.featuredVehicleIds),
        ...(source.featuredVehicleLayout ? { featuredVehicleLayout: source.featuredVehicleLayout } : {}),
        archived: false,
        status: 'draft',
        scheduledAt: null,
        repeat: parseCampaignRepeat(source.repeat),
        sendOccurrence: 0,
        sentCount: 0,
        failedCount: 0,
        unsubscribedCount: 0,
        sentAt: null,
        createdByUserId: uid,
        createdAt: now,
        updatedAt: now,
      }
      const docRef = await addDoc(db.marketing_campaigns, payload)
      return docRef.id
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: marketingCampaignsQueryKey })
      showSuccess('Campaign duplicated as a new draft')
    },
    onError: (err) =>
      showError(err instanceof Error ? err.message : 'Could not duplicate campaign'),
  })

  const archiveMutation = useMutation({
    mutationFn: async (input: { campaignId: string; archived: boolean }) => {
      const current = await loadCampaign(input.campaignId)
      await writeCampaign({ ...current, archived: input.archived })
    },
    onSuccess: async (_data, input) => {
      await queryClient.invalidateQueries({ queryKey: marketingCampaignsQueryKey })
      showSuccess(input.archived ? 'Campaign archived' : 'Campaign restored')
    },
    onError: (err) =>
      showError(err instanceof Error ? err.message : 'Could not update archive status'),
  })

  const sendMutation = useMutation({
    mutationFn: async (campaignId: string) => {
      let remaining = Number.POSITIVE_INFINITY
      let last = {
        campaignId,
        status: 'sending' as 'sending' | 'sent',
        eligibleCount: 0,
        queuedThisBatch: 0,
        sentThisBatch: 0,
        failedThisBatch: 0,
        sentTotal: 0,
        remaining: 0,
      }
      let rounds = 0
      while (remaining > 0 && rounds < 100) {
        rounds += 1
        try {
          last = await portalApi.admin.sendMarketingCampaign({
            campaignId,
            maxRecipients: MARKETING_SEND_BATCH_SIZE,
          })
        } catch (error) {
          const message = error instanceof Error ? error.message : String(error)
          if (/paused|cancelled/i.test(message)) {
            return last
          }
          throw error
        }
        remaining = last.remaining
        if (last.status === 'sent') break
      }
      return last
    },
    onSuccess: async (result) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: marketingCampaignsQueryKey }),
        queryClient.invalidateQueries({ queryKey: contactsQueryKey }),
        queryClient.invalidateQueries({ queryKey: campaignRecipientsQueryKey }),
      ])
      const sent = result.sentTotal.toLocaleString('en-US')
      if (result.failedThisBatch > 0) {
        showSuccess(
          `Sent ${sent} of ${result.eligibleCount.toLocaleString('en-US')}. Some recipients failed and can be retried.`,
        )
      } else {
        showSuccess(`Sent to ${sent} recipient${result.sentTotal === 1 ? '' : 's'}`)
      }
    },
    onError: (err) =>
      showError(err instanceof Error ? err.message : 'Could not send campaign'),
  })

  const sendTestMutation = useMutation({
    mutationFn: async (campaignId: string) => {
      return portalApi.admin.sendMarketingCampaignTest({ campaignId })
    },
    onSuccess: (result) => {
      showSuccess(`Test email sent to ${result.to}`)
    },
    onError: (err) =>
      showError(err instanceof Error ? err.message : 'Could not send test email'),
  })

  const loadCampaign = async (campaignId: string): Promise<MarketingCampaign> => {
    const snap = await getDoc(doc(db.marketing_campaigns, campaignId))
    const current = snap.data()
    if (!current) throw new Error('Campaign not found')
    return current
  }

  const writeCampaign = async (campaign: MarketingCampaign) => {
    await setDoc(doc(db.marketing_campaigns, campaign.id), {
      ...campaign,
      updatedAt: new Date(),
    })
  }

  const scheduleMutation = useMutation({
    mutationFn: async (input: { id: string; scheduledAt: Date; repeat?: CampaignRepeat }) => {
      const scheduledAt = input.scheduledAt
      if (!(scheduledAt instanceof Date) || Number.isNaN(scheduledAt.getTime())) {
        throw new Error('Pick a date and time.')
      }
      if (scheduledAt.getTime() <= Date.now()) {
        throw new Error('Pick a future date and time.')
      }
      const current = await loadCampaign(input.id)
      if (current.status !== 'draft' && current.status !== 'scheduled') {
        throw new Error('Only draft or scheduled campaigns can be scheduled.')
      }
      if (!current.subject.trim()) {
        throw new Error('Subject is required before scheduling.')
      }
      await writeCampaign({
        ...current,
        status: 'scheduled',
        scheduledAt,
        repeat: parseCampaignRepeat(input.repeat ?? current.repeat),
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: marketingCampaignsQueryKey })
      showSuccess('Campaign scheduled')
    },
    onError: (err) =>
      showError(err instanceof Error ? err.message : 'Could not schedule campaign'),
  })

  const pauseMutation = useMutation({
    mutationFn: async (campaignId: string) => {
      const current = await loadCampaign(campaignId)
      if (current.status !== 'scheduled' && current.status !== 'sending') {
        throw new Error('Only scheduled or sending campaigns can be paused.')
      }
      await writeCampaign({ ...current, status: 'paused' })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: marketingCampaignsQueryKey })
      showSuccess('Campaign paused')
    },
    onError: (err) =>
      showError(err instanceof Error ? err.message : 'Could not pause campaign'),
  })

  const resumeMutation = useMutation({
    mutationFn: async (campaignId: string): Promise<CampaignStatus> => {
      const current = await loadCampaign(campaignId)
      if (current.status !== 'paused') {
        throw new Error('Only paused campaigns can be resumed.')
      }
      const scheduledAt = current.scheduledAt
      const stillInFuture =
        scheduledAt instanceof Date &&
        !Number.isNaN(scheduledAt.getTime()) &&
        scheduledAt.getTime() > Date.now()
      const nextStatus: CampaignStatus = stillInFuture ? 'scheduled' : 'sending'
      await writeCampaign({ ...current, status: nextStatus })
      return nextStatus
    },
    onSuccess: async (nextStatus) => {
      await queryClient.invalidateQueries({ queryKey: marketingCampaignsQueryKey })
      if (nextStatus === 'scheduled') {
        showSuccess('Campaign scheduled again')
      }
    },
    onError: (err) =>
      showError(err instanceof Error ? err.message : 'Could not resume campaign'),
  })

  const cancelMutation = useMutation({
    mutationFn: async (campaignId: string) => {
      const current = await loadCampaign(campaignId)
      if (
        current.status !== 'scheduled' &&
        current.status !== 'sending' &&
        current.status !== 'paused'
      ) {
        throw new Error('This campaign cannot be cancelled.')
      }
      await writeCampaign({ ...current, status: 'cancelled' })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: marketingCampaignsQueryKey })
      showSuccess('Campaign cancelled')
    },
    onError: (err) =>
      showError(err instanceof Error ? err.message : 'Could not cancel campaign'),
  })

  const returnToDraftMutation = useMutation({
    mutationFn: async (campaignId: string) => {
      const current = await loadCampaign(campaignId)
      const sentCount = current.sentCount ?? 0
      const canReturn =
        current.status === 'scheduled' ||
        (current.status === 'paused' && sentCount === 0)
      if (!canReturn) {
        throw new Error('Only scheduled campaigns that have not sent can return to draft.')
      }
      await writeCampaign({
        ...current,
        status: 'draft',
        scheduledAt: null,
      })
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: marketingCampaignsQueryKey })
      showSuccess('Campaign returned to draft')
    },
    onError: (err) =>
      showError(err instanceof Error ? err.message : 'Could not return campaign to draft'),
  })

  const resumeCampaign = async (campaignId: string) => {
    const nextStatus = await resumeMutation.mutateAsync(campaignId)
    if (nextStatus === 'sending') {
      await sendMutation.mutateAsync(campaignId)
    }
  }

  return {
    campaigns: campaigns ?? [],
    isLoading,
    error: error instanceof Error ? error : error ? new Error(String(error)) : null,
    createCampaign: (input: CreateMarketingCampaignInput) =>
      createMutation.mutateAsync(input),
    updateCampaign: (input: UpdateMarketingCampaignInput) =>
      updateMutation.mutateAsync(input),
    duplicateCampaign: (sourceId: string) => duplicateMutation.mutateAsync(sourceId),
    archiveCampaign: (campaignId: string) =>
      archiveMutation.mutateAsync({ campaignId, archived: true }),
    unarchiveCampaign: (campaignId: string) =>
      archiveMutation.mutateAsync({ campaignId, archived: false }),
    sendCampaign: (campaignId: string) => sendMutation.mutateAsync(campaignId),
    sendTestCampaign: (campaignId: string) => sendTestMutation.mutateAsync(campaignId),
    scheduleCampaign: (input: { id: string; scheduledAt: Date; repeat?: CampaignRepeat }) =>
      scheduleMutation.mutateAsync(input),
    pauseCampaign: (campaignId: string) => pauseMutation.mutateAsync(campaignId),
    resumeCampaign,
    cancelCampaign: (campaignId: string) => cancelMutation.mutateAsync(campaignId),
    returnCampaignToDraft: (campaignId: string) =>
      returnToDraftMutation.mutateAsync(campaignId),
    isSaving:
      createMutation.isPending ||
      updateMutation.isPending ||
      duplicateMutation.isPending,
    isArchiving: archiveMutation.isPending,
    isSending: sendMutation.isPending,
    isSendingTest: sendTestMutation.isPending,
    isUpdatingStatus:
      scheduleMutation.isPending ||
      pauseMutation.isPending ||
      resumeMutation.isPending ||
      cancelMutation.isPending ||
      returnToDraftMutation.isPending,
  }
}
